'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Blocks duplicate AI/API submissions (double-clicks) and enforces a cooldown after each run.
 * Use `disabled` for button `disabled` props; wrap async work in `run()`.
 */
export function useAiActionCooldown(cooldownMs = 4000) {
  const lockRef = useRef(false);
  const cooldownUntilRef = useRef(0);
  const [inFlight, setInFlight] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);

  useEffect(() => {
    if (cooldownUntil <= 0) return undefined;
    const delay = Math.max(0, cooldownUntil - Date.now());
    const id = window.setTimeout(() => {
      cooldownUntilRef.current = 0;
      setCooldownUntil(0);
    }, delay);
    return () => window.clearTimeout(id);
  }, [cooldownUntil]);

  const disabled = inFlight || Date.now() < cooldownUntil;

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      if (lockRef.current || Date.now() < cooldownUntilRef.current) {
        return undefined;
      }
      lockRef.current = true;
      setInFlight(true);
      try {
        return await fn();
      } finally {
        lockRef.current = false;
        setInFlight(false);
        const until = Date.now() + cooldownMs;
        cooldownUntilRef.current = until;
        setCooldownUntil(until);
      }
    },
    [cooldownMs]
  );

  return { disabled, run, inFlight };
}
